<template>
  <div id="delete-account-wrap">
    <p>We're sorry to see you go. Please read the following information carefully before proceeding with account deletion.</p>

    <div v-if="!pinSent">
      <ul>
        <li>Your account and all associated data will be permanently deleted.</li>
        <li>Any purchased products or services will no longer be accessible.</li>
        <li>Some data may be retained for 90 days for legal and operational purposes.</li>
      </ul>

      <label>
        <input type="checkbox" v-model="confirmUnderstanding">
        I understand that this action is irreversible and I will lose access to all my data and purchases.
      </label>

      <input 
        type="button" 
        @click="initiateAccountDeletion" 
        :disabled="!confirmUnderstanding" 
        class="fa-btn fa-btn-primary fa-btn-modal" 
        value="Request Account Deletion"
      >
    </div>

    <form v-else-if="!confirmationSent" @submit.prevent="confirmAccountDeletion">
      <p>A verification PIN has been sent to your email. Please enter it below to confirm your account deletion request.</p>
      <Input 
        type="text" 
        name="pin" 
        id="pin" 
        label="Verification PIN" 
        v-model="pin" 
      />
      <input 
        type="submit" 
        class="fa-btn fa-btn-primary fa-btn-modal" 
        value="Confirm Account Deletion"
      >
      <ModalMessage :message="feedbackMessage" :type="feedbackType" style="margin-top: 1rem;"/>
    </form>

    <div v-else>
      <p>Your account deletion request has been confirmed. Your account is scheduled for deletion in 14 days.</p>
      <p>If you change your mind, simply log in to your account within the next 14 days to cancel the deletion process. If you have any questions, please contact our support team immediately.</p>
      <ModalMessage :message="feedbackMessage" :type="feedbackType" style="margin-top: 1rem;"/>
    </div>

   
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useUserStore } from '~/store/userStore';
import { MessageType } from '@/types/enums.ts';

const userStore = useUserStore();

const confirmUnderstanding = ref(false);
const pinSent = ref(false);
const confirmationSent = ref(false);
const feedbackMessage = ref('');
const feedbackType = ref(MessageType.INFO);
const pin = ref('');
const requestId = ref('');

const currentEmail = computed(() => userStore.userData?.email_address || '');

const initiateAccountDeletion = async () => {
  try {
    const response = await fetch(`${import.meta.env.VITE_WP_BASE_URL}/wp-json/fa/v1/initiate-account-deletion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': import.meta.env.VITE_WP_TOKEN
      },
      body: JSON.stringify({ email: currentEmail.value })
    });

    const data = await response.json();

    if (data.success) {
      pinSent.value = true;
      requestId.value = data.request_id;
      feedbackMessage.value = 'A verification PIN has been sent to your email. Please check your inbox.';
      feedbackType.value = MessageType.SUCCESS;
    } else {
      feedbackMessage.value = data.message || 'Failed to initiate account deletion. Please try again.';
      feedbackType.value = MessageType.ERROR;
    }
  } catch (error) {
    console.error('Error initiating account deletion:', error);
    feedbackMessage.value = 'An error occurred. Please try again later.';
    feedbackType.value = MessageType.ERROR;
  }
};

const confirmAccountDeletion = async () => {
  try {
    const response = await fetch(`${import.meta.env.VITE_WP_BASE_URL}/wp-json/fa/v1/confirm-account-deletion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': import.meta.env.VITE_WP_TOKEN
      },
      body: JSON.stringify({ 
        email: currentEmail.value,
        pin: pin.value,
        request_id: requestId.value
      })
    });

    const data = await response.json();

    if (data.success) {
      confirmationSent.value = true;
      feedbackMessage.value = data.message;
      feedbackType.value = MessageType.SUCCESS;
    } else {
      feedbackMessage.value = data.message || 'Failed to confirm account deletion. Please try again.';
      feedbackType.value = MessageType.ERROR;
    }
  } catch (error) {
    console.error('Error confirming account deletion:', error);
    feedbackMessage.value = 'An error occurred. Please try again later.';
    feedbackType.value = MessageType.ERROR;
  }
};
</script>